import placements, { Placement } from '../placements';

import prependProtocol from './prepend-protocol';

const getAnchorTarget = (brazeTarget: string): string => {
  switch (brazeTarget) {
    case 'BLANK':
      return '_blank';
    case 'SELF':
      return '_self';
    default:
      return '_self';
  }
};

interface Button {
  id: string;
  uri: string;
  text: string;
  clickAction: string;
  ariaLabel?: string;
}

export interface PrescreenPayload {
  imageUrl: string;
  buttons: Button[];
  extras: {
    buttonColor: string;
    template?: string;
    mobile_image_url?: string;
    removeCrossIcon?: boolean;
    button1AriaLabel?: string;
    button2AriaLabel?: string;
    button1Color?: string;
    button2Color?: string;
    title?: string;
    altImage?: string;
  };
  triggerId: string;
  uri: string;
  clickAction: string;
  openTarget: string;
  isHTML: boolean;
  message: string;
  removeCrossIcon?: boolean;
}

export interface TeaserPayload {
  imageUrl: string;
  url: string;
  linkText: string;
  id: string;
  extras: {
    openTarget: string;
  };
}

export interface SlideUpPayload {
  duration: number;
  dismissType: 'AUTO_DISMISS' | 'SWIPE';
  uri: string;
  message: string;
  clickAction: string;
  openTarget: string;
  triggerId: string;
  imageUrl: string;
  extras: {
    button_text?: string;
    altImage?: string;
    title?: string;
  };
}

interface PrescreenResult {
  buttons: Button[];
  campaignId: string;
  messageUrl?: string | null | undefined;
  messageClickAction: string;
  buttonText: string;
  buttonColor: string;
  title?: string;
  imageUrl: string;
  altImage?: string;
  openTarget: string;
  hasButton: boolean;
  isHTML: boolean;
  message: string;
  template?: string;
  mobileImageURL?: string;
  removeCrossIcon?: boolean;
  button1AriaLabel?: string;
  button2AriaLabel?: string;
  button1Color?: string;
  button2Color?: string;
}

interface TeaserResult {
  campaignId: string;
  url?: string | null | undefined;
  linkText: string;
  imageUrl: string;
  openTarget: string;
}

interface SlideUpResult {
  url: string;
  campaignId: string;
  messageDuration: number;
  dismissType: 'AUTO_DISMISS' | 'SWIPE';
  message: string;
  clickAction: string;
  openTarget: string;
  imageUrl: string;
  altImage?: string;
  buttonText?: string;
}

type DestructureResult =
  | PrescreenResult
  | TeaserResult
  | SlideUpResult
  | PrescreenPayload
  | TeaserPayload
  | SlideUpPayload;

const destructure = (
  placement: Placement,
  payload: PrescreenPayload | TeaserPayload | SlideUpPayload
): DestructureResult => {
  switch (placement) {
    case placements.PRESCREEN: {
      const {
        extras,
        imageUrl,
        buttons,
        triggerId: campaignId,
        uri: messageUrl,
        clickAction: messageClickAction,
        openTarget,
        isHTML,
        message,
      } = payload as PrescreenPayload;
      const hasButton = buttons && buttons.length > 0;
      const {
        buttonColor,
        button1Color,
        button2Color,
        button1AriaLabel,
        button2AriaLabel,
        altImage,
        template,
        title,
        mobile_image_url: mobileImageURL,
        removeCrossIcon,
      } = extras;
      const urlButtons =
        hasButton && buttons.filter((button) => button.clickAction === 'URI');
      const {
        uri: buttonUrl,
        text,
        clickAction: buttonClickAction,
      }: any = (urlButtons && urlButtons.length > 0 && urlButtons[0]) || {};
      return {
        buttons,
        campaignId,
        // We always prefer the button-Settings as the Braze interface doesn't
        // show the message- Setting anymore if you add a Button.
        messageUrl: prependProtocol(buttonUrl || messageUrl),
        messageClickAction: buttonClickAction || messageClickAction,
        buttonText: text,
        buttonColor,
        title,
        imageUrl,
        altImage,
        openTarget: getAnchorTarget(openTarget ?? 'SELF'),
        hasButton,
        isHTML,
        message,
        template,
        mobileImageURL,
        removeCrossIcon,
        button1AriaLabel,
        button2AriaLabel,
        button1Color,
        button2Color,
      };
    }
    case placements.TEASER: {
      const {
        imageUrl,
        url,
        linkText,
        id: campaignId,
        extras: { openTarget },
      } = payload as TeaserPayload;
      return {
        campaignId,
        url: prependProtocol(url),
        linkText,
        imageUrl,
        openTarget: getAnchorTarget(openTarget ?? 'BLANK'),
      };
    }
    case placements.SLIDE_UP: {
      const {
        uri: url,
        duration: messageDuration,
        dismissType,
        triggerId: campaignId,
        message,
        clickAction,
        openTarget,
        imageUrl,
        extras: { button_text: buttonText, altImage, title },
      } = payload as SlideUpPayload;
      return {
        url,
        campaignId,
        messageDuration,
        dismissType,
        message,
        clickAction,
        openTarget: getAnchorTarget(openTarget ?? 'SELF'),
        imageUrl,
        altImage,
        title,
        buttonText,
      };
    }
    default:
      return payload;
  }
};

export default destructure;
